/**
 * Downgrade Confirmation Modal - Trade Loop v2.0
 *
 * Modal that explains the downgrade process and confirms user intent
 * - Shows what features will be lost
 * - Explains no proration/refund policy
 * - Shows effective date (next billing cycle)
 * - Allows user to confirm or cancel
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AlertTriangle, Info, Calendar, ArrowDownCircle } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';

interface DowngradeConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentPlan: string;
  targetPlan: string;
  effectiveDate?: Date;
  isLoading?: boolean;
}

const PLAN_FEATURES: Record<string, string[]> = {
  grow: [
    'Team collaboration tools',
    'Custom integrations',
    'Organization seats',
    'Dedicated account manager',
    '24/7 premium support',
  ],
  pro: [
    'Professional profile',
    'Verified badge',
    'Unlimited trades',
    'Partner favorites',
    'Priority support',
  ],
  starter: [
    'Basic profile only',
    'Limited to 1 trade',
  ],
};

export function DowngradeConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  currentPlan,
  targetPlan,
  effectiveDate,
  isLoading = false,
}: DowngradeConfirmationModalProps) {
  const { showWarning } = useToast();
  const [isConfirming, setIsConfirming] = useState(false);

  const currentPlanName = currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1);
  const targetPlanName = targetPlan.charAt(0).toUpperCase() + targetPlan.slice(1);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Get features that will be lost
  const getFeaturesLost = () => {
    const currentFeatures = PLAN_FEATURES[currentPlan] || [];
    const targetFeatures = PLAN_FEATURES[targetPlan] || [];

    // Features in current but not in target = lost features
    return currentFeatures.filter(feature => !targetFeatures.includes(feature));
  };

  const featuresLost = getFeaturesLost();

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
      onClose();
    } catch (error: any) {
      console.error('Downgrade confirmation error:', error);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowDownCircle className="w-5 h-5 text-amber-600" />
            Confirm Plan Downgrade
          </DialogTitle>
          <DialogDescription>
            You are changing from <strong>{currentPlanName}</strong> to <strong>{targetPlanName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Important Info Box */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-semibold text-amber-900 text-sm">Important Information</h4>
              </div>
            </div>

            <ul className="space-y-2 text-sm text-amber-800 ml-7">
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">•</span>
                <span>
                  <strong>No refunds or prorations:</strong> You will not receive a refund for the remaining time on your current billing period.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">•</span>
                <span>
                  <strong>Access retained:</strong> You'll keep all features of your current plan until the next billing date.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">•</span>
                <span>
                  <strong>Scheduled change:</strong> The downgrade will take effect automatically at the end of your billing period.
                </span>
              </li>
            </ul>
          </div>

          {/* Effective Date */}
          {effectiveDate && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <Calendar className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-blue-900 text-sm mb-1">Effective Date</h4>
                <p className="text-sm text-blue-700">
                  Your subscription will change to {targetPlanName} on{' '}
                  <strong>{formatDate(effectiveDate)}</strong>
                </p>
              </div>
            </div>
          )}

          {/* Features Lost */}
          {featuresLost.length > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-2 mb-3">
                <Info className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm">Features You'll Lose</h4>
                  <p className="text-xs text-gray-600 mt-1">
                    These features will no longer be available after {effectiveDate ? formatDate(effectiveDate) : 'the downgrade'}
                  </p>
                </div>
              </div>
              <ul className="space-y-1.5 ml-7">
                {featuresLost.map((feature, index) => (
                  <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                    <span className="text-red-500 font-bold">✗</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isConfirming || isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isConfirming || isLoading}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {isConfirming || isLoading ? 'Scheduling...' : 'Confirm Downgrade'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
